import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '../hooks/useAuth';
import { workspacesApi, articlesApi, accountsApi } from '../lib/api';
import { Spinner, StatCard } from '@rankhelm/ui';
import type { Workspace, Article, Account } from '@rankhelm/shared';
import { workspaceTitles } from '../lib/workspace';

export function DashboardPage() {
  const { user, account } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [articleStats, setArticleStats] = useState({ total: 0, drafts: 0, inReview: 0, published: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadSuperAdmin() {
      try {
        const res = await accountsApi.list();
        const data = Array.isArray(res.data) ? res.data : res.data ? [res.data] : [];
        setAccounts(data);
      } catch (e) {
        console.error('Failed to load accounts:', e);
      } finally {
        setLoading(false);
      }
    }

    async function loadStandard() {
      try {
        const wsRes = await workspacesApi.list();
        const workspaceList: Workspace[] = wsRes.data || [];
        setWorkspaces(workspaceList);

        if (workspaceList.length > 0) {
          const articlePromises = workspaceList.map((w) =>
            articlesApi.list(w.id).catch(() => ({ data: [] as Article[] }))
          );
          const articleResults = await Promise.all(articlePromises);
          const allArticles: Article[] = articleResults.flatMap((r) => r.data || []);

          setArticleStats({
            total: allArticles.length,
            drafts: allArticles.filter((a) => a.status === 'draft' || a.status === 'queued' || a.status === 'generating').length,
            inReview: allArticles.filter((a) => a.status === 'in_review' || a.status === 'revision_requested').length,
            published: allArticles.filter((a) => a.status === 'published').length,
          });
        }
      } catch (e) {
        console.error('Failed to load dashboard data:', e);
      } finally {
        setLoading(false);
      }
    }

    if (isSuperAdmin) {
      loadSuperAdmin();
    } else {
      loadStandard();
    }
  }, [isSuperAdmin]);

  if (loading) {
    return <Spinner message="Loading dashboard..." fullPage />;
  }

  if (isSuperAdmin) {
    return (
      <div>
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Platform overview</h1>
          <p className="text-slate-400 mt-1">
            Super admin — {user?.email}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
          <StatCard label="Accounts" value={accounts.length} />
          <StatCard
            label="Agency accounts"
            value={accounts.filter((a) => a.type === 'agency').length}
          />
          <StatCard
            label="Enterprise accounts"
            value={accounts.filter((a) => a.type === 'enterprise').length}
          />
        </div>

        <div className="bg-navy-900 border border-navy-700 rounded-xl p-6 mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">Quick actions</h2>
          <div className="flex gap-3 flex-wrap">
            <Link
              to="/accounts"
              className="px-4 py-2 bg-helm-500 hover:bg-helm-400 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Manage accounts
            </Link>
            <Link
              to="/settings"
              className="px-4 py-2 bg-navy-800 hover:bg-navy-700 text-slate-300 text-sm font-medium rounded-lg border border-navy-600 transition-colors"
            >
              Platform settings
            </Link>
          </div>
        </div>

        {accounts.length > 0 && (
          <div className="bg-navy-900 border border-navy-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Recent accounts</h2>
              <Link to="/accounts" className="text-xs text-helm-400 hover:text-helm-300 transition-colors">
                View all →
              </Link>
            </div>
            <div className="space-y-3">
              {accounts.slice(0, 5).map((acct) => (
                <Link
                  key={acct.id}
                  to={`/accounts/${acct.id}`}
                  className="block px-4 py-3 bg-navy-800 hover:bg-navy-700 rounded-lg transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">{acct.name}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{acct.slug}</p>
                    </div>
                    <span className="text-xs text-slate-500">{acct.type || 'individual'}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Welcome back, {user?.name?.split(' ')[0]}</h1>
        <p className="text-slate-400 mt-1">
          {account?.name} — {user?.role?.replace('_', ' ')}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
        <StatCard label="Workspaces" value={workspaces.length} />
        <StatCard label="Articles" value={articleStats.total} detail={
          <div className="flex items-center gap-4">
            <span className="text-xs"><span className="text-white">{articleStats.drafts}</span> <span className="text-blue-400/60">drafts</span></span>
            <span className="text-xs"><span className="text-white">{articleStats.inReview}</span> <span className="text-orange-400/60">in review</span></span>
            <span className="text-xs"><span className="text-white">{articleStats.published}</span> <span className="text-emerald-400/60">published</span></span>
          </div>
        } />
      </div>

      {/* Quick Actions */}
      <div className="bg-navy-900 border border-navy-700 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
        <div className="flex gap-3 flex-wrap">
          <Link
            to="/workspaces"
            className="px-4 py-2 bg-helm-500 hover:bg-helm-400 text-white text-sm font-medium rounded-lg transition-colors"
          >
            View Workspaces
          </Link>
          {user?.role === 'account_admin' && (
            <Link
              to="/workspaces/new"
              className="px-4 py-2 bg-navy-800 hover:bg-navy-700 text-slate-300 text-sm font-medium rounded-lg border border-navy-600 transition-colors"
            >
              Add Workspace
            </Link>
          )}
        </div>
      </div>

      {/* Recent Workspaces */}
      {workspaces.length > 0 && (
        <div className="mt-8 bg-navy-900 border border-navy-700 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Recent Workspaces</h2>
          <div className="space-y-3">
            {workspaces.slice(0, 5).map((ws) => {
              const titles = workspaceTitles(ws);
              return (
              <Link
                key={ws.id}
                to={`/workspaces/${ws.id}`}
                className="block px-4 py-3 bg-navy-800 hover:bg-navy-700 rounded-lg transition-colors"
              >
                <p className="text-white font-medium">{titles.primary}</p>
                {titles.secondary && (
                  <p className="text-xs text-slate-400 mt-0.5">{titles.secondary}</p>
                )}
              </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
